<template>
  <v-card outlined>
    <!-- Card header -->
    <v-card-title class="py-3 px-4">
      <v-icon left color="primary"> mdi-package-variant </v-icon>
      <span class="text-subtitle-1 font-weight-bold">Inventory Monitoring</span>
    </v-card-title>
    <v-divider />

    <!-- Toolbar -->
    <v-card-text class="pb-0 pt-3">
      <div
        class="d-flex align-start justify-space-between flex-wrap"
        style="gap: 12px"
      >
        <!-- Left: search + filters + recalculate -->
        <div class="d-flex align-center flex-wrap" style="gap: 8px">
          <v-btn
            v-if="authPermissions.has('inventory_monitoring:recalculate')"
            color="primary"
            small
            @click="recalculate"
          >
            <v-icon left small> mdi-calculator </v-icon>
            Recalculate
          </v-btn>

          <v-text-field
            v-model="search"
            outlined
            dense
            hide-details
            placeholder="Search..."
            prepend-inner-icon="mdi-magnify"
            style="min-width: 200px; max-width: 260px"
          />

          <v-text-field
            v-model="dateStart"
            label="Date Start"
            type="date"
            dense
            hide-details
            outlined
            clearable
            style="max-width: 175px"
          />

          <v-text-field
            v-model="dateEnd"
            label="Date End"
            type="date"
            dense
            hide-details
            outlined
            clearable
            style="max-width: 175px"
          />
        </div>

        <!-- Right: export buttons + column selector -->
        <div class="d-flex align-start flex-wrap" style="gap: 8px">
          <v-btn small color="success" :loading="fetching" @click="exportExcel">
            <v-icon left small> mdi-microsoft-excel </v-icon>
            Excel
          </v-btn>
          <v-btn small color="error" :loading="fetching" @click="exportPdf">
            <v-icon left small> mdi-file-pdf-box </v-icon>
            PDF
          </v-btn>
          <v-autocomplete
            v-model="visibleColumns"
            :items="headerColumnList"
            item-text="text"
            item-value="value"
            label="Visible Columns"
            multiple
            clearable
            dense
            hide-details
            outlined
            chips
            style="min-width: 260px; max-width: 360px"
          >
            <template #item="{ item, on, attrs }">
              <v-list-item v-bind="attrs" v-on="on">
                <v-list-item-content>
                  <v-list-item-title>{{ item.text }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
            <template #selection="{ item, index }">
              <v-chip v-if="item" :key="index" class="ma-1" x-small outlined>
                {{ item.text }}
              </v-chip>
            </template>
          </v-autocomplete>
        </div>
      </div>
    </v-card-text>

    <!-- Table -->
    <v-card-text class="pa-0 pt-2">
      <v-data-table
        :headers="headers"
        :items="inventoryItems"
        :options.sync="options"
        :server-items-length="serverItemsLength"
        :loading="fetching"
        dense
        :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100, -1] }"
      >
        <template #body.prepend="{}">
          <tr>
            <td
              class="text-left font-weight-bold"
              colspan="5"
              style="border-bottom: 2px solid #bdbdbd"
            >
              Total Worth
            </td>
            <td
              class="text-right font-weight-bold"
              style="border-bottom: 2px solid #bdbdbd"
            >
              {{ formatAccounting(inventoryTotalWorth) }}
            </td>
            <td
              style="border-bottom: 2px solid #bdbdbd"
              :colspan="headers.length - 5 < 0 ? 0 : headers.length - 5"
            />
          </tr>
        </template>

        <template #item.worth="{ item }">
          <span
            :class="
              item.retail * (item.current_quantity || 0) < 0
                ? 'red--text font-weight-bold'
                : 'green--text'
            "
          >
            {{ formatAccounting(item.retail * (item.current_quantity || 0)) }}
          </span>
        </template>

        <template #item.current_quantity="{ value }">
          <v-chip
            x-small
            :color="!value ? 'red' : value < 0 ? 'red' : 'green'"
            dark
            v-text="value < 0 ? '(' + Math.abs(value) + ')' : value"
          />
        </template>

        <template #item.action="{ item }">
          <div class="d-flex align-center" style="gap: 2px">
            <v-tooltip
              v-if="authPermissions.has('inventory_monitoring:recalculate')"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  icon
                  x-small
                  v-on="on"
                  @click="recalculateInventory(item)"
                >
                  <v-icon small color="purple"> mdi-calculator </v-icon>
                </v-btn>
              </template>
              Recalculate Item
            </v-tooltip>

            <v-tooltip
              v-if="authPermissions.has('inventory_monitoring:recalculate')"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  icon
                  x-small
                  v-on="on"
                  @click="getInventoryBreakdown(item.stock_id)"
                >
                  <v-icon small color="primary"> mdi-table </v-icon>
                </v-btn>
              </template>
              View Inventory Breakdown
            </v-tooltip>

            <v-tooltip
              v-if="
                authPermissions.has('inventory_monitoring_restore_point:create')
              "
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  icon
                  x-small
                  :loading="item.creatingRestorePoint"
                  v-on="on"
                  @click="createRestorePoint(item)"
                >
                  <v-icon small color="green"> mdi-restore </v-icon>
                </v-btn>
              </template>
              Create Restore Point
            </v-tooltip>

            <v-tooltip
              v-if="authPermissions.has('inventory_monitoring:set')"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  icon
                  x-small
                  v-on="on"
                  @click="$emit('edit', item.id)"
                >
                  <v-icon small color="amber darken-2"> mdi-pencil </v-icon>
                </v-btn>
              </template>
              Edit
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- Breakdown Dialog -->
    <v-dialog v-model="dialog" max-width="860">
      <v-card>
        <v-toolbar color="primary" dark dense>
          <v-icon left> mdi-table-search </v-icon>
          <span class="text-subtitle-2">Quantity Remaining Breakdown</span>
          <v-spacer />
          <v-btn icon small @click="dialog = false">
            <v-icon> mdi-close </v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-0">
          <v-simple-table dense>
            <template #default>
              <thead>
                <tr>
                  <th class="text-left">Type</th>
                  <th class="text-left">Ref</th>
                  <th class="text-right">Qty</th>
                  <th class="text-right">Qty Remaining</th>
                  <th class="text-left">Unit</th>
                  <th class="text-left">Consumer</th>
                  <th class="text-left">Notes</th>
                  <th class="text-left">Actual Date</th>
                  <th class="text-left">Timestamp</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in inventoryBreakdownItems" :key="item.id">
                  <td>
                    <v-chip
                      x-small
                      label
                      dark
                      :color="journalTypeConf(item.type).color"
                    >
                      <v-icon x-small left>
                        {{ journalTypeConf(item.type).icon }}
                      </v-icon>
                      {{ journalTypeConf(item.type).label }}
                    </v-chip>
                  </td>
                  <td>
                    <a
                      v-if="item.sourceId"
                      class="text-decoration-underline"
                      style="cursor: pointer"
                      @click="$refs.sourceRefDialog.open(item.id)"
                    >{{ item.sourceRef }}</a>
                    <span v-else>{{ item.sourceRef }}</span>
                  </td>
                  <td class="text-right">
                    <span
                      :class="
                        item.direction === 'in'
                          ? 'success--text font-weight-medium'
                          : 'error--text font-weight-medium'
                      "
                    >
                      {{ item.direction === 'in' ? '+' : '-'
                      }}{{ formatQty(item.quantity) }}
                    </span>
                  </td>
                  <td class="text-right font-weight-medium">
                    {{ formatQty(item.balance) }}
                  </td>
                  <td>{{ item.unit }}</td>
                  <td class="text-caption text-no-wrap">
                    {{ item.consumerName }}
                  </td>
                  <td class="text-caption grey--text">
                    {{ item.notes }}
                  </td>
                  <td class="text-caption text-no-wrap">
                    {{ formatActualDate(item.actual_date) }}
                  </td>
                  <td class="text-caption text-no-wrap">
                    {{ formatTs(item.created_at) }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <source-ref-dialog ref="sourceRefDialog" />
  </v-card>
</template>
<script>
import axios from '@/extra-functions/axios.extra'
import { mapState } from 'vuex'
import {
  downloadCSV,
  ErrorPopup,
  ConfirmPopup,
  ConfirmDialog,
  formatAccounting,
  LoadingPopup,
  SuccessPopup,
  useWatchEffect,
} from '../../../../lib/utils'
import StoreInventoryPdf from '../../../../pdf/store-inventory.pdf'
import appAxios from '../../../../extra-functions/axios.extra'
import { debugLog } from '@/lib/electron-utils.js'
import _ from 'lodash'

const [watchEffect, clearWatchEffect] = useWatchEffect()

function resolveSourceRef(type, src, sourceId) {
  if (!src) return sourceId ?? '—'
  if (type === 'credit' || type === 'credit_reversal')
    return src.consumer_loan_id ?? sourceId ?? '—'
  return src.receipt_no ?? src.csi_number ?? sourceId ?? '—'
}

const JOURNAL_TYPE_CONF = {
  sale: { label: 'Cash Sale', icon: 'mdi-cash-register', color: 'blue' },
  purchase: {
    label: 'Purchase',
    icon: 'mdi-cart-arrow-down',
    color: 'orange darken-1',
  },
  transfer: {
    label: 'Transfer Out',
    icon: 'mdi-transfer-right',
    color: 'purple',
  },
  receive: { label: 'Received', icon: 'mdi-transfer-down', color: 'teal' },
  loss: {
    label: 'Stock Loss',
    icon: 'mdi-alert-circle-outline',
    color: 'red darken-1',
  },
  credit: {
    label: 'Credit Sale',
    icon: 'mdi-credit-card-outline',
    color: 'indigo',
  },
  adjustment: { label: 'Adjustment', icon: 'mdi-tune', color: 'brown' },
  initial: {
    label: 'Initial Stock',
    icon: 'mdi-flag-checkered',
    color: 'blue-grey',
  },
  sale_promo: {
    label: 'Promo Sale',
    icon: 'mdi-tag-heart',
    color: 'cyan darken-1',
  },
  return: { label: 'Good Return', icon: 'mdi-keyboard-return', color: 'green' },
  return_bad_order: {
    label: 'Bad Order Return',
    icon: 'mdi-package-variant-remove',
    color: 'deep-orange',
  },
  sale_reversal: {
    label: 'Sale Reversal',
    icon: 'mdi-undo',
    color: 'blue-grey darken-1',
  },
  purchase_reversal: {
    label: 'Purchase Reversal',
    icon: 'mdi-undo',
    color: 'blue-grey darken-1',
  },
  transfer_reversal: {
    label: 'Transfer Reversal',
    icon: 'mdi-undo',
    color: 'blue-grey darken-1',
  },
  receive_reversal: {
    label: 'Receive Reversal',
    icon: 'mdi-undo',
    color: 'blue-grey darken-1',
  },
  loss_reversal: {
    label: 'Loss Reversal',
    icon: 'mdi-undo',
    color: 'blue-grey darken-1',
  },
  credit_reversal: {
    label: 'Credit Reversal',
    icon: 'mdi-undo',
    color: 'blue-grey darken-1',
  },
  adjustment_reversal: {
    label: 'Adj. Reversal',
    icon: 'mdi-undo',
    color: 'blue-grey darken-1',
  },
  initial_reversal: {
    label: 'Initial Reversal',
    icon: 'mdi-undo',
    color: 'blue-grey darken-1',
  },
  sale_promo_reversal: {
    label: 'Promo Reversal',
    icon: 'mdi-undo',
    color: 'blue-grey darken-1',
  },
  return_reversal: {
    label: 'Return Reversal',
    icon: 'mdi-undo',
    color: 'blue-grey darken-1',
  },
  return_bad_order_reversal: {
    label: 'Bad Order Rev.',
    icon: 'mdi-undo',
    color: 'blue-grey darken-1',
  },
}

export default {
  name: 'InventoryTable',
  components: {
    SourceRefDialog: () => import('./SourceRefDialog.vue'),
  },
  data() {
    return {
      recalculating: false,
      fetching: false,
      inventoryItems: [],
      search: '',
      page: 1,
      headerFilter: [],
      visibleColumns: [],
      inventoryBreakdownItems: [],
      dialog: false,
      dateStart: null,
      dateEnd: null,
      serverItemsLength: 0,
      options: {
        itemsPerPage: 25,
      },
      indexController: null,
    }
  },
  created() {
    this.getIndex()
    this.visibleColumns = this.headerColumnList.map((col) => col.value) // show all by default
  },
  destroyed() {
    clearWatchEffect()
  },
  computed: {
    ...mapState({
      authToken: (state) => state.auth.token,
      currentStoreId: (state) => state.settings.storeId,
      otherStores: (state) =>
        state.branchLocation.list.filter(
          (item) => item.id !== state.settings.storeId,
        ),
      branchLocations: (state) =>
        state.branchLocation.list.reduce((acc, item) => {
          acc[item.id] = item.name
          return acc
        }, {}),
      authPermissions: (state) => state.auth.userPermissions,
    }),
    dateRangeText() {
      if (!this.dateRange?.length) return ''
      const [start, end] = this.dateRange
      return `${start} → ${end}`
    },
    headerColumnList() {
      const otherStores = this.otherStores.map((store) => ({
        text: store.name,
        value: `store_${store.id}`,
      }))
      const columns = [
        { text: 'Branch', value: 'store', width: 'wrap' },
        {
          text: 'Code',
          value: 'stock_code',
          sortable: true,
        },
        {
          text: 'Item',
          value: 'item',
        },
        {
          text: 'Brand',
          value: 'brand_name',
        },
        {
          text: 'SRP',
          value: 'retail',
          align: 'right',
        },
        {
          text: 'Worth',
          value: 'worth',
          align: 'right',
        },
        {
          text: 'Unit',
          value: 'unit',
          align: 'center',
        },
        ...otherStores,
        {
          text: 'Actual Qty',
          value: 'current_quantity',
          align: 'center',
          sortable: true,
        },
        { text: 'Action', value: 'action', align: 'center' },
      ]
      return columns
    },
    headers() {
      const allColumns = [...this.headerColumnList]
      if (!this.visibleColumns || this.visibleColumns.length === 0) {
        return allColumns // fallback to all if none selected
      }
      const visibleCols = new Set([...this.visibleColumns])
      debugLog('visibleCols', [...visibleCols])
      return allColumns.filter((col) => visibleCols.has(col.value))
    },
    inventoryTotalWorth() {
      return this.inventoryItems.reduce(
        (prev, next) => prev + next.retail * (next.current_quantity || 0),
        0,
      )
    },
  },
  watch: {
    options: {
      handler() {
        this.getIndex()
      },
      deep: true,
    },
    search() {
      this.options.page = 1
      this.getIndex()
    },
    dateStart() {
      this.getIndex()
    },
    dateEnd() {
      this.getIndex()
    },
  },
  methods: {
    async recalculate() {
      try {
        if (this.recalculating) return
        LoadingPopup.fire({ text: 'Recalculating inventory, please wait...' })
        this.recalculating = true
        await axios(this.authToken).post('/inventories-v2/recalculate')
        this.getIndex()
        SuccessPopup.fire({ text: 'Recalculation completed successfully!' })
      } catch (e) {
        console.error(e.message)
        ErrorPopup.fire()
        // this.$alertSwalError('oops something went wrong!')
      } finally {
        LoadingPopup.close()
        this.recalculating = false
      }
    },
    getIndex: _.debounce(function () {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => controller?.abort?.())
          this.fetching = true

          const { page, itemsPerPage, sortBy, sortDesc } = this.options

          const params = {
            page,
            date_start: this.dateStart,
            date_end: this.dateEnd,
            per_page: itemsPerPage,
            search: this.search,
            sort_by: sortBy?.[0] || null,
            sort_desc: sortDesc?.[0] || false,
          }

          const { data } = await axios(this.authToken).get('/v2/inventories', {
            params,
            signal: controller?.signal,
          })
          this.inventoryItems = data.data
          this.serverItemsLength = data.total
        } catch (e) {
          console.error(e)
        } finally {
          this.fetching = false
        }
      }, 'InventoryTable__getIndex')
    }, 500),

    getReport: _.throttle(
      /**
       * Description
       * @param {AbortController} controller
       */
      async function (controller) {
        try {
          this.fetching = true

          const { page, itemsPerPage, sortBy, sortDesc } = this.options

          const params = _.omitBy(
            {
              page,
              per_page: itemsPerPage,
              search: this.search,
              sort_by: sortBy?.[0] || null,
              sort_desc: sortDesc?.[0] || false,
              date_start: this.dateStart || null,
              date_end: this.dateEnd || null,
            },
            (v) => !v,
          )

          const { data } = await axios(this.authToken).get(
            '/v2/inventories/report',
            {
              params,
              signal: controller?.signal,
            },
          )
          return data
        } catch (e) {
          console.error(e)
        } finally {
          this.fetching = false
        }
      },
      1000,
    ),
    customFilter(_, search, item) {
      if (!search) return true
      const searchRegEx = new RegExp(search, 'i')
      if (searchRegEx.test(item?.item)) return true
      if (searchRegEx.test(item?.unit)) return true
      if (searchRegEx.test(item?.stock_code)) return true
      return false
    },
    async recalculateInventory(item) {
      try {
        if (!item.stock_id) throw new Error('item.stock_id is undefined')
        const { isConfirmed } = await ConfirmDialog.fire({
          title: 'Recalculate Inventory',
          html: `<p>Are you sure you want to recalculate inventory for <b>${item.item}</b>?</p>`,
        })
        if (!isConfirmed) return
        LoadingPopup.fire({ text: 'Recalculating Inventory...' })
        item.recalculating = true
        this.inventoryItems = [...this.inventoryItems]
        await axios(this.authToken).post(
          `/stocks/${item.stock_id}/recalculate-inventory`,
        )
        this.getIndex()
        SuccessPopup.fire({
          text: `successfully recalculated inventory for ${item.item}`,
        })
      } catch (e) {
        console.error(e.message)
        ErrorPopup.fire()
      } finally {
        item.recalculating = false
        this.inventoryItems = [...this.inventoryItems]
      }
    },
    async createRestorePoint(item) {
      try {
        if (!item.stock_id) throw new Error('item.stock_id is undefined')
        const { isConfirmed } = await ConfirmDialog.fire({
          title: 'Create Restore Point',
          html: `<p>Are you sure you want to create a restore point for <b>${item.item}</b>?</p>`,
        })
        if (!isConfirmed) return
        item.creatingRestorePoint = true
        this.inventoryItems = [...this.inventoryItems]
        await axios(this.authToken).post(
          `/stocks/${item.stock_id}/restore-point`,
        )
        this.$alertSwalSuccess(
          `successfully created an inventory restore point for ${item.item}`,
        )
      } catch (e) {
        console.error(e.message)
        this.$alertSwalError('oops something went wrong')
      } finally {
        item.creatingRestorePoint = false
        this.inventoryItems = [...this.inventoryItems]
      }
    },
    exportExcel() {
      watchEffect(async (onCleanup) => {
        const controller = new AbortController()
        onCleanup(() => {
          controller.abort()
        })
        const data = await this.getReport(controller)
        const otherStores = this.otherStores.map((store) => ({
          text: store.name,
          value: `store_${store.id}`,
        }))
        const headers = [
          'store',
          'stock code',
          'srp',
          'category',
          'description',
          'brand name',
          'unit',
          ...otherStores.map((s) => s.text),
          'quantity',
        ]
        const rows = data.map((item) => [
          item.store,
          item.stock_code,
          item.retail,
          item.category || '',
          item.item,
          item.brand_name,
          item.unit,
          ...otherStores.map((s) => item[s.value] ?? ''),
          item.current_quantity,
        ])
        const storeName = data?.[0]?.store?.replace?.(' ', '_')?.toLowerCase?.()
        const csvFilename = `${storeName}_inventory_excel_${new Date().getTime()}.csv`
        downloadCSV([headers, ...rows], csvFilename)
      }, 'exportGetIndex')
    },
    exportPdf() {
      watchEffect(async (onCleanup) => {
        const controller = new AbortController()
        onCleanup(() => {
          controller.abort()
        })
        const data = await this.getReport(controller)
        const getRowTextColor = (quantity) => {
          if (quantity === 0) return '#777777'
          if (quantity < 0) return '#FF0000'
          if (quantity <= 10) return '#F5BF08'
          if (quantity > 10) return '#265E1C'
          throw new Error('invalid quantity argument')
        }
        const rows = data.map((item) => {
          const textColor = getRowTextColor(item.current_quantity)
          return [
            { content: item.stock_code, styles: { textColor } },
            {
              content: item.item || '',
              styles: { textColor },
            },
            { content: item.brand_name, styles: { textColor } },
            {
              content:
                (item.current_quantity < 0
                  ? `(${Math.abs(item.current_quantity)})`
                  : item.current_quantity) +
                ' ' +
                item.unit,
              styles: { textColor },
            },
          ]
        })
        const pdf = new StoreInventoryPdf()
        pdf.generate(rows)
      }, 'exportGetIndex')
    },
    async getInventoryBreakdown(stockId) {
      try {
        LoadingPopup.fire({
          text: 'Please wait while I retrieve the inventory breakdown...',
        })
        const { data } = await appAxios(this.authToken).get(
          '/v2/inventory-journals',
          {
            params: { stock_id: stockId, per_page: 500 },
          },
        )
        const items = data.data ?? []
        this.inventoryBreakdownItems = items.map((r) => {
          const src = r.source
          const sourceRef = resolveSourceRef(r.type, src, r.source_id)
          return {
            id: r.id,
            type: r.type,
            direction: r.direction,
            quantity: r.quantity,
            unit: r.unit,
            balance: Math.round(Number(r.balance || 0) * 100) / 100,
            sourceRef,
            sourceId: r.source_id,
            consumerName: r.consumer_name || '—',
            notes: r.notes ?? '',
            actual_date: r.actual_date,
            created_at: r.created_at,
          }
        })
        LoadingPopup.close()
        this.dialog = true
      } catch (e) {
        console.error(e)
        ErrorPopup.fire()
      }
    },
    formatAccounting,

    journalTypeConf(type) {
      return (
        JOURNAL_TYPE_CONF[type] ?? {
          label: type,
          icon: 'mdi-help-circle-outline',
          color: 'grey',
        }
      )
    },

    formatQty(val) {
      return Number(val || 0).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    },

    formatTs(val) {
      if (!val) return '—'
      return (val + '').dateFormat({ hour: '2-digit', minute: '2-digit' })
    },

    formatActualDate(val) {
      if (!val) return '—'
      return (val + '').dateFormat()
    },
  },
}
</script>
